// The two-phase BYOK capture (arc 101 slice 02) — credential first, then the
// provider's OWN model list. Split from `<AddKeyModal/>` the way
// `createKeyRotate` is split from the key routes: this is the state machine, the
// modal is the shell, and both key pools drive the same one so the org and
// personal surfaces cannot drift on what a capture sends.
//
// Why the order changed: the model id used to be free text, which is how prod
// grew `gpt-3.5-turbo`, `kimi-k2`, `glm-4.6` and `glm-4.7` on live rows — and
// how the 2026-08-12 incident happened, three keys re-probed every two minutes
// on the customer's money until a human revoked them. A select over what the
// provider actually serves is the fix for that cause.

import { BYOKProvider } from '@developerz/domain';
import { batch, createSignal } from 'solid-js';
import { captureModels } from './keyModels';
import { type ProbeRefusal, probeRefusal } from './keyProbeRefusal';
import type { AddKeyInput, ProbeModelsInput, ProviderModelOption } from './keys';
import { createModelTags, type ModelTagsController } from './modelTags';
import { needsBaseUrl, wantsBaseUrl } from './providerEndpoint';

/** `credential` = provider + endpoint + key; `models` = pick one and tag tiers. */
export type CapturePhase = 'credential' | 'models';

/**
 * Why the endpoint field holds the credential back, before any request: absent
 * where the provider requires one, or not a URL at all (the input's own `type=url`
 * constraint). Whether a well-formed endpoint is one the provider is RECOGNIZED at
 * stays the server's refusal — this is the part a form can know on its own.
 */
export type BaseUrlFault = 'missing' | 'malformed';

export type KeyCaptureController = {
  readonly phase: () => CapturePhase;
  readonly provider: () => AddKeyInput['provider'];
  readonly setProvider: (value: string) => void;
  readonly key: () => string;
  readonly setKey: (value: string) => void;
  readonly baseUrl: () => string;
  readonly setBaseUrl: (value: string) => void;
  readonly wantsBaseUrl: () => boolean;
  readonly needsBaseUrl: () => boolean;
  /** The endpoint field's own refusal, or null. Cleared by an edit or a provider switch. */
  readonly baseUrlFault: () => BaseUrlFault | null;
  /** Raise a fault the INPUT detected (its constraint validation fired `invalid`). */
  readonly flagBaseUrl: (fault: BaseUrlFault) => void;
  readonly credentialIncomplete: () => boolean;
  readonly probing: () => boolean;
  /** The framed refusal of the last enumeration, or null. */
  readonly refusal: () => ProbeRefusal | null;
  /** The provider's answer — ids AND the window it reported for each, or none. */
  readonly models: () => readonly ProviderModelOption[];
  readonly model: () => string;
  readonly setModel: (value: string) => void;
  readonly tags: ModelTagsController;
  readonly listModels: () => void;
  readonly back: () => void;
  readonly submit: () => void;
  readonly reset: () => void;
};

export function createKeyCapture(deps: {
  /** `probeProviderModels` (org) or `probeMyProviderModels` (personal). */
  readonly probe: (input: ProbeModelsInput) => Promise<readonly ProviderModelOption[]>;
  readonly onSubmit: (input: AddKeyInput) => void;
}): KeyCaptureController {
  const [phase, setPhase] = createSignal<CapturePhase>('credential');
  const [provider, setProvider] = createSignal<AddKeyInput['provider']>(BYOKProvider.Anthropic);
  const [key, setKey] = createSignal('');
  const [baseUrl, setBaseUrl] = createSignal('');
  const [baseUrlFault, setBaseUrlFault] = createSignal<BaseUrlFault | null>(null);
  const [probing, setProbing] = createSignal(false);
  const [refusal, setRefusal] = createSignal<ProbeRefusal | null>(null);
  const [models, setModels] = createSignal<readonly ProviderModelOption[]>([]);
  const [model, setModel] = createSignal('');
  const tags = createModelTags();

  // The endpoint rule is `lib/providerEndpoint.ts`'s — one seam, because the
  // donate form gates the same field and the API refines the same pairing.
  const wantsUrl = (): boolean => wantsBaseUrl(provider());
  const needsUrl = (): boolean => needsBaseUrl(provider());

  // The KEY alone holds the button shut. A missing required endpoint does not:
  // a disabled button says nothing about why, so the ask stays live and the
  // submit answers with the field's own error instead — before any request.
  const credentialIncomplete = (): boolean => key().trim() === '';

  const endpoint = (): { base_url?: string } => {
    const url = baseUrl().trim();
    return wantsUrl() && url !== '' ? { base_url: url } : {};
  };

  const run = async (): Promise<void> => {
    if (credentialIncomplete() || probing()) return;
    if (needsUrl() && baseUrl().trim() === '') {
      setBaseUrlFault('missing');
      return;
    }
    setProbing(true);
    setRefusal(null);
    try {
      const found = await deps.probe({ provider: provider(), key: key().trim(), ...endpoint() });
      // A new list is a new vocabulary: a model picked — or a tier tagged —
      // against the previous credential names nothing this one can address.
      batch(() => {
        tags.clear();
        setModel('');
        setModels(found);
        setPhase('models');
      });
    } catch (e) {
      // Stays on the credential phase with the fields intact, so every refusal
      // is retryable by construction — including the permanent ones, whose copy
      // is what says retrying will not help.
      setRefusal(probeRefusal(e));
    } finally {
      setProbing(false);
    }
  };

  return {
    phase,
    provider,
    setProvider: (value) =>
      batch(() => {
        setProvider(value as AddKeyInput['provider']);
        setBaseUrlFault(null);
      }),
    key,
    setKey,
    baseUrl,
    setBaseUrl: (value) =>
      batch(() => {
        setBaseUrl(value);
        setBaseUrlFault(null);
      }),
    wantsBaseUrl: wantsUrl,
    needsBaseUrl: needsUrl,
    baseUrlFault,
    flagBaseUrl: setBaseUrlFault,
    credentialIncomplete,
    probing,
    refusal,
    models,
    model,
    setModel,
    tags,
    listModels: () => void run(),
    back: () => setPhase('credential'),
    submit: () => {
      if (phase() !== 'models' || model() === '') return;
      // `captureModels` owns the whole rule — tags, probed windows, ceiling.
      // `undefined` when there is nothing to say: omitted asks the server for
      // the one untagged catalogue row, and `[]` is a 400, not a synonym.
      const catalogue = captureModels(model(), tags.tags, models());
      deps.onSubmit({
        provider: provider(),
        model: model(),
        key: key().trim(),
        ...endpoint(),
        ...(catalogue === undefined ? {} : { models: catalogue }),
      });
    },
    reset: () =>
      batch(() => {
        setPhase('credential');
        setProvider(BYOKProvider.Anthropic);
        setKey('');
        setBaseUrl('');
        setBaseUrlFault(null);
        setRefusal(null);
        setModels([]);
        setModel('');
        tags.clear();
      }),
  };
}
